import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Input, Button, Spin, Typography, Tag, message as antMessage, Tooltip } from 'antd';
import {
  SendOutlined,
  RobotOutlined,
  UserOutlined,
  DeleteOutlined,
  LoadingOutlined,
  ThunderboltOutlined,
  ApiOutlined,
  SearchOutlined,
  ToolOutlined,
  PlusCircleOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';
import {
  sendAgentMessage,
  clearAgentSession,
  ChatMessageItem,
  CardPayload,
} from '../../services/agentApi';
import { MessageCards } from './MessageCards';
import './styles.css';

const { TextArea } = Input;
const { Text, Title } = Typography;

const QUICK_COMMANDS = [
  { label: '我的协议有哪些？', icon: <ApiOutlined /> },
  { label: '查询设备状态', icon: <ThunderboltOutlined /> },
  { label: '查询对接状态', icon: <ToolOutlined /> },
  { label: '搜索设备', icon: <SearchOutlined /> },
  { label: '编辑设备', icon: <EditOutlined /> },
  { label: '录入设备', icon: <PlusCircleOutlined /> },
];

interface ChatPanelProps {
  compact?: boolean;
}

export const ChatPanel: React.FC<ChatPanelProps> = ({ compact = false }) => {
  const [messages, setMessages] = useState<ChatMessageItem[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [toolStatus, setToolStatus] = useState<string | null>(null);
  const isInitializingRef = useRef(false);

  // 页面刷新后恢复已持久化的 sessionId
  useEffect(() => {
    if (isInitializingRef.current) return;
    isInitializingRef.current = true;
    const savedId = localStorage.getItem('agent_session_id');
    if (savedId) {
      setSessionId(savedId);
    }
  }, []);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const assistantIdRef = useRef<string | null>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, toolStatus, scrollToBottom]);

  const handleSend = async (text?: string) => {
    const content = (text || input).trim();
    if (!content || loading) return;

    setInput('');
    setLoading(true);
    setToolStatus(null);

    const userMsg: ChatMessageItem = {
      id: uuidv4(),
      role: 'user',
      content,
    };

    const assistantId = uuidv4();
    assistantIdRef.current = assistantId;
    const assistantMsg: ChatMessageItem = {
      id: assistantId,
      role: 'assistant',
      content: '',
      loading: true,
      cards: [],
    };

    setMessages((prev) => [...prev, userMsg, assistantMsg]);

    let accumulatedContent = '';
    const cards: CardPayload[] = [];

    await sendAgentMessage(content, sessionId, {
      onSession: (id) => setSessionId(id),
      onToolStatus: (label) => setToolStatus(label),
      onContent: (chunk) => {
        accumulatedContent = typeof chunk === 'string' ? chunk : String(chunk);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? { ...m, content: accumulatedContent, loading: true }
              : m
          )
        );
      },
      onCard: (card) => {
        cards.push(card);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId ? { ...m, cards: [...cards] } : m
          )
        );
      },
      onError: (errMsg) => {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? { ...m, content: errMsg, loading: false, error: true }
              : m
          )
        );
        antMessage.error(errMsg);
      },
      onDone: () => {
        setLoading(false);
        setToolStatus(null);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? {
                  ...m,
                  content: accumulatedContent || m.content || '已完成',
                  loading: false,
                  cards: cards.length ? cards : m.cards,
                }
              : m
          )
        );
      },
    });
  };

  const handleClear = async () => {
    if (sessionId) {
      await clearAgentSession(sessionId);
    }
    setMessages([]);
    setSessionId(null);
    setToolStatus(null);
    antMessage.success('对话已清空');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className={`agent-chat-panel ${compact ? 'compact' : ''}`}>
      <header className="agent-chat-header">
        <div className="agent-chat-header-left">
          <div className="agent-chat-logo">
            <RobotOutlined />
          </div>
          <div className="agent-chat-header-text">
            <Title level={5} className="agent-chat-title">
              智能助手
            </Title>
            <Text type="secondary" className="agent-chat-subtitle">
              综合数据管理系统 · AI 对话
            </Text>
          </div>
          {sessionId && (
            <Tag className="agent-session-tag" bordered={false}>
              <span className="agent-session-dot" />
              已连接
            </Tag>
          )}
        </div>
        <Tooltip title="清空当前对话">
          <Button
            type="text"
            className="agent-clear-btn"
            icon={<DeleteOutlined />}
            onClick={handleClear}
            disabled={messages.length === 0}
          >
            清空
          </Button>
        </Tooltip>
      </header>

      {!compact && messages.length === 0 && (
        <div className="agent-quick-commands">
          {QUICK_COMMANDS.map((cmd) => (
            <button
              key={cmd.label}
              type="button"
              className="agent-quick-chip"
              disabled={loading}
              onClick={() => handleSend(cmd.label)}
            >
              <span className="agent-quick-chip-icon">{cmd.icon}</span>
              <span>{cmd.label}</span>
            </button>
          ))}
        </div>
      )}

      <div className="agent-messages">
        {messages.length === 0 && (
          <div className="agent-empty">
            <div className="agent-empty-icon">
              <RobotOutlined />
            </div>
            <Title level={4} className="agent-empty-title">
              你好，有什么可以帮你？
            </Title>
            <Text type="secondary" className="agent-empty-desc">
              我可以帮你查询协议、设备状态、对接日志，或协助完成设备配置操作
            </Text>
            {!compact && (
              <div className="agent-empty-suggestions">
                {QUICK_COMMANDS.slice(0, 4).map((cmd) => (
                  <button
                    key={cmd.label}
                    type="button"
                    className="agent-suggestion-card"
                    disabled={loading}
                    onClick={() => handleSend(cmd.label)}
                  >
                    <span className="agent-suggestion-icon">{cmd.icon}</span>
                    <span className="agent-suggestion-label">{cmd.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`agent-message agent-message-${msg.role} ${msg.error ? 'error' : ''}`}
          >
            <div className="agent-message-avatar" aria-hidden="true">
              {msg.role === 'user' ? <UserOutlined /> : <RobotOutlined />}
            </div>
            <div className="agent-message-body">
              {msg.loading && !msg.content ? (
                <div className="agent-message-content agent-message-typing">
                  <span className="agent-typing-dot" />
                  <span className="agent-typing-dot" />
                  <span className="agent-typing-dot" />
                </div>
              ) : (
                <div className="agent-message-content">
                  {msg.content.split('\n').map((line, i, arr) => (
                    <React.Fragment key={i}>
                      {line}
                      {i < arr.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </div>
              )}
              {msg.cards && msg.cards.length > 0 && (
                <MessageCards cards={msg.cards} />
              )}
            </div>
          </div>
        ))}

        {toolStatus && (
          <div className="agent-tool-status">
            <Spin size="small" />
            <Text type="secondary">{toolStatus}</Text>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <footer className="agent-input-area">
        <div className="agent-input-wrapper">
          <TextArea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="输入您的问题…"
            autoSize={{ minRows: 1, maxRows: 4 }}
            disabled={loading}
            bordered={false}
            className="agent-input-field"
          />
          <Button
            type="primary"
            shape="circle"
            icon={<SendOutlined />}
            onClick={() => handleSend()}
            loading={loading}
            disabled={!input.trim()}
            className="agent-send-btn"
            aria-label="发送消息"
          />
        </div>
        <Text type="secondary" className="agent-input-hint">
          Enter 发送 · Shift+Enter 换行
        </Text>
      </footer>
    </div>
  );
};

export default ChatPanel;
